import { useCallback, useEffect, useMemo } from 'react';
import type { Email } from '@/types/email';
import type { BriefingItem } from '@/api/briefing';
import { useBriefingStore, type BriefingScope } from '@/stores/briefingStore';
import {
  STALE_AFTER_HOURS,
  formatDueChip,
  kindSortIndex,
  type BriefingRelatedRef,
} from '../components/briefingShared';
import type { BriefingRowRef } from '../components/BriefingRows';

/**
 * The union the briefing-mode virtualizer renders: collapsible kind section
 * headers with the kind's item rows beneath them.
 */
export type BriefingListRow =
  | { type: 'email'; email: Email }
  | { type: 'briefingItem'; item: BriefingItem; refs: BriefingRowRef[]; related: BriefingRelatedRef[] }
  | { type: 'briefingSection'; kind: string; count: number; collapsed: boolean };

export interface BriefingHeaderData {
  generatedAt: string;
  itemCount: number;
  notes: string[];
  isStale: boolean;
  /** Kinds present in the current briefing, sorted, with active-item counts. */
  kindCounts: Array<{ kind: string; count: number }>;
  /** Items whose due chip reads overdue / due today (order-by, ETA, … included). */
  overdueCount: number;
  dueTodayCount: number;
}

interface UseBriefingRowsOptions {
  enabled: boolean;
  accountId: string | null;
  /** The live visible-list projection — preferred source for ref emails. */
  emails: Email[];
}

interface UseBriefingRowsResult {
  /** Pinned rows (item rows first, then collapsed kind rows). */
  briefingRows: BriefingListRow[];
  /** Email ids referenced by active items — hidden from the regular list. */
  suppressedIds: Set<string>;
  header: BriefingHeaderData | null;
  scope: BriefingScope;
  /** Resolved item + refs for an active item drill-down scope. */
  scopeItem: BriefingItem | null;
  scopeItemRefs: BriefingRowRef[];
  pendingKeys: Set<string>;
  isLoading: boolean;
  refsForItem: (item: BriefingItem) => BriefingRowRef[];
  /** data.related_keys resolved to live items (self excluded, dead keys dropped). */
  relatedForItem: (item: BriefingItem) => BriefingRelatedRef[];
  /** Lookup for opening a related item's drill-down from its key. */
  itemByKey: Map<string, BriefingItem>;
}

const EMPTY_SET: Set<string> = new Set();

const sortItems = (a: BriefingItem, b: BriefingItem): number =>
  a.priority - b.priority ||
  kindSortIndex(a.kind) - kindSortIndex(b.kind) ||
  a.title.localeCompare(b.title);

export function useBriefingRows({ enabled, accountId, emails }: UseBriefingRowsOptions): UseBriefingRowsResult {
  const entries = useBriefingStore((state) => state.entries);
  const refEmails = useBriefingStore((state) => state.refEmails);
  const collapsedKinds = useBriefingStore((state) => state.collapsedKinds);
  const scope = useBriefingStore((state) => state.scope);
  const pendingKeys = useBriefingStore((state) => state.pendingKeys);
  const isLoading = useBriefingStore((state) => state.isLoading);
  const fetchForAccount = useBriefingStore((state) => state.fetchForAccount);

  useEffect(() => {
    if (enabled && accountId) {
      void fetchForAccount(accountId);
    }
  }, [enabled, accountId, fetchForAccount]);

  const liveById = useMemo(() => {
    const map = new Map<string, Email>();
    if (!enabled) {
      return map;
    }
    emails.forEach((email) => {
      if (!map.has(email.id)) {
        map.set(email.id, email);
      }
    });
    return map;
  }, [enabled, emails]);

  const allItems = useMemo(
    () => (enabled && entries ? entries.flatMap((entry) => entry.items) : []),
    [enabled, entries]
  );

  const itemByKey = useMemo(() => {
    const map = new Map<string, BriefingItem>();
    for (const item of allItems) {
      map.set(item.key, item);
    }
    return map;
  }, [allItems]);

  const relatedForItem = useCallback(
    (item: BriefingItem): BriefingRelatedRef[] => {
      const keys = item.data?.related_keys;
      if (!Array.isArray(keys)) {
        return [];
      }
      const related: BriefingRelatedRef[] = [];
      for (const key of keys) {
        if (typeof key !== 'string' || key === item.key) continue;
        const target = itemByKey.get(key);
        if (target) {
          related.push({ key, title: target.title });
        }
      }
      return related;
    },
    [itemByKey]
  );

  const refsForItem = useCallback(
    (item: BriefingItem): BriefingRowRef[] => {
      const refs: BriefingRowRef[] = [];
      for (const ref of item.messageRefs) {
        if (!ref.message_id) continue;
        const messageId = String(ref.message_id);
        const refAccountId = ref.account_id ?? item.accountId;
        const hydrated = refEmails[`${refAccountId}:${messageId}`];
        const live = liveById.get(messageId);
        if (live) {
          // Live projection copy wins (fresh read/flag state); membership info
          // comes from the hydration (a live inbox-list copy implies inbox).
          refs.push({ email: live, inInbox: hydrated?.inInbox ?? true, isLive: true });
        } else if (hydrated) {
          refs.push({ email: hydrated.email, inInbox: hydrated.inInbox, isLive: false });
        }
        // Neither: the referenced message no longer exists — dropped ref.
      }
      return refs;
    },
    [refEmails, liveById]
  );

  const briefingRows = useMemo<BriefingListRow[]>(() => {
    if (!enabled || allItems.length === 0) {
      return [];
    }
    // Item drill-down replaces the email list itself; no pinned rows.
    if (scope?.type === 'item') {
      return [];
    }
    if (scope?.type === 'kind') {
      return allItems
        .filter((item) => item.kind === scope.kind)
        .sort(sortItems)
        .map((item) => ({
          type: 'briefingItem' as const,
          item,
          refs: refsForItem(item),
          related: relatedForItem(item),
        }));
    }

    // Top level: one collapsible section per kind, items beneath.
    const byKind = new Map<string, BriefingItem[]>();
    for (const item of allItems) {
      const bucket = byKind.get(item.kind);
      if (bucket) {
        bucket.push(item);
      } else {
        byKind.set(item.kind, [item]);
      }
    }

    const kinds = Array.from(byKind.keys()).sort((a, b) => kindSortIndex(a) - kindSortIndex(b));
    const rows: BriefingListRow[] = [];
    for (const kind of kinds) {
      const items = byKind.get(kind)!.sort(sortItems);
      const collapsed = Boolean(collapsedKinds[kind]);
      rows.push({ type: 'briefingSection', kind, count: items.length, collapsed });
      if (!collapsed) {
        for (const item of items) {
          rows.push({
            type: 'briefingItem',
            item,
            refs: refsForItem(item),
            related: relatedForItem(item),
          });
        }
      }
    }
    return rows;
  }, [enabled, allItems, scope, collapsedKinds, refsForItem, relatedForItem]);

  // Every active item's refs are hidden from the regular list below —
  // including refs of collapsed kinds (their drill-down shows them). Message
  // ids are globally unique, so an id set suffices across accounts. Scopes
  // render their own lists, so no suppression applies there.
  const suppressedIds = useMemo(() => {
    if (!enabled || scope || allItems.length === 0) {
      return EMPTY_SET;
    }
    const set = new Set<string>();
    for (const item of allItems) {
      for (const ref of item.messageRefs) {
        if (ref.message_id) {
          set.add(String(ref.message_id));
        }
      }
    }
    return set;
  }, [enabled, scope, allItems]);

  const header = useMemo<BriefingHeaderData | null>(() => {
    if (!enabled || !entries || entries.length === 0) {
      return null;
    }
    const latest = entries.reduce((a, b) =>
      new Date(a.briefing.generatedAt) > new Date(b.briefing.generatedAt) ? a : b
    );
    const notes = entries
      .map((entry) => entry.briefing.notes)
      .filter((value): value is string => Boolean(value && value.trim()));

    const countByKind = new Map<string, number>();
    let overdueCount = 0;
    let dueTodayCount = 0;
    for (const item of allItems) {
      countByKind.set(item.kind, (countByKind.get(item.kind) ?? 0) + 1);
      const due = typeof item.data?.due === 'string' ? item.data.due : null;
      if (due) {
        const dueType = typeof item.data?.due_type === 'string' ? item.data.due_type : null;
        const { overdue, today } = formatDueChip(due, dueType);
        if (overdue) {
          overdueCount++;
        } else if (today) {
          dueTodayCount++;
        }
      }
    }
    const kindCounts = Array.from(countByKind.entries())
      .sort((a, b) => kindSortIndex(a[0]) - kindSortIndex(b[0]))
      .map(([kind, count]) => ({ kind, count }));

    return {
      generatedAt: latest.briefing.generatedAt,
      itemCount: allItems.length,
      notes,
      isStale:
        Date.now() - new Date(latest.briefing.generatedAt).getTime() >
        STALE_AFTER_HOURS * 3600 * 1000,
      kindCounts,
      overdueCount,
      dueTodayCount,
    };
  }, [enabled, entries, allItems]);

  const scopeItem = useMemo(() => {
    if (!enabled || scope?.type !== 'item') {
      return null;
    }
    return (
      allItems.find((item) => item.accountId === scope.accountId && item.key === scope.key) ?? null
    );
  }, [enabled, scope, allItems]);

  const scopeItemRefs = useMemo(
    () => (scopeItem ? refsForItem(scopeItem) : []),
    [scopeItem, refsForItem]
  );

  return {
    briefingRows,
    suppressedIds,
    header,
    scope: enabled ? scope : null,
    scopeItem,
    scopeItemRefs,
    pendingKeys,
    isLoading,
    refsForItem,
    relatedForItem,
    itemByKey,
  };
}
